// ============================================================
// THE LENS (the bench's loupe; the ruling: the lens on every figure): a viewer
// who turned it on in Settings gets a round magnifier following the pointer
// over a plate, showing the figure's own drawing (`#<u>-figure`) three times
// over. The person plate and the vehicle plate share it (plan design-gaps, P8).
// ============================================================

import { useState, useSyncExternalStore, type PointerEvent } from 'react';
import type { Frame } from '../../engine/plate/calloutLayout';
import { getViewerPrefs, subscribeViewerPrefs } from '../../utils/viewerPrefs';
import styles from '../../styles/BodyPlate.module.css';

const LENS = { r: 420, zoom: 3 };
const lensWanted = () => getViewerPrefs().bodyLens === true;
const LENS_TICKS = `M${-LENS.r - 60} 0 h80 M${LENS.r - 20} 0 h80 M0 ${-LENS.r - 60} v80 M0 ${LENS.r - 20} v80`;

/**
 * A pointer's place in the plate's frame. The svg keeps its aspect (xMidYMid meet), so the frame is scaled by the
 * tighter side and centred; null before the svg has a size.
 */
function framePoint(e: PointerEvent<SVGSVGElement>, vb: Frame): [number, number] | null {
  const rect = e.currentTarget.getBoundingClientRect();
  const scale = Math.min(rect.width / vb.w, rect.height / vb.h);
  if (!(scale > 0)) return null;
  const left = rect.left + (rect.width - vb.w * scale) / 2;
  const top = rect.top + (rect.height - vb.h * scale) / 2;
  return [vb.x + (e.clientX - left) / scale, vb.y + (e.clientY - top) / scale];
}

/** Where the lens is (null when off or the pointer is away), and the pointer handlers the plate's svg takes. */
export function useLens(vb: Frame) {
  const on = useSyncExternalStore(subscribeViewerPrefs, lensWanted, lensWanted);
  const [at, setAt] = useState<[number, number] | null>(null);
  return {
    at: on ? at : null,
    handlers: {
      onPointerMove: on ? (e: PointerEvent<SVGSVGElement>) => setAt(framePoint(e, vb)) : undefined,
      onPointerLeave: () => setAt(null),
    },
  };
}

/** The lens at a point of the frame: the figure under it, magnified, in a ringed circle with its ticks. */
export function Lens({ u, at: [x, y] }: { u: string; at: [number, number] }) {
  return (
    <g pointerEvents="none" transform={`translate(${x} ${y})`} data-testid="plate-lens">
      <clipPath id={`${u}-lens`}>
        <circle r={LENS.r} />
      </clipPath>
      <g clipPath={`url(#${u}-lens)`}>
        <circle className={styles.ground} r={LENS.r} />
        <g transform={`scale(${LENS.zoom}) translate(${-x} ${-y})`}>
          <use href={`#${u}-figure`} />
        </g>
      </g>
      <circle className={styles.lensRim} r={LENS.r} />
      <path className={styles.lensRim} d={LENS_TICKS} />
    </g>
  );
}
